import { Rng } from '../../engine/math/random';
import type { ItemId } from '../items/ItemDefs';

/**
 * Mode B (§6.1): every item except the Sword and Chalice spawns at a random candidate spot.
 * Spots live in "zones"; a zone is reachable once the items it requires are obtainable.
 * `solvable()` is a reachability fixpoint over that dependency graph; `shuffle()` resamples until solvable.
 */
export type Zone =
  | 'hearth' | 'goldKeep' | 'marshNear' | 'marshFar' | 'dunesOpen' | 'dunesBuried' | 'dunesCaged'
  | 'ironwood' | 'whiteKeep' | 'cisternNear' | 'cisternFar' | 'blackKeep';

/** What you must be able to obtain before a zone's contents are reachable. */
export const ZONE_NEEDS: Record<Zone, ItemId[]> = {
  hearth: [], goldKeep: ['goldKey'], marshNear: [], marshFar: ['plank'],
  dunesOpen: [], dunesBuried: ['magnet'], dunesCaged: ['magnet'],
  ironwood: [], whiteKeep: ['whiteKey'], cisternNear: [], cisternFar: ['plank'], blackKeep: ['blackKey', 'plank'],
};

export interface Spot { id: string; zone: Zone }

/** Candidate spots per shuffled item (3–5 each, across at least two biomes). */
export const CANDIDATES: Record<Exclude<ItemId, 'sword' | 'chalice' | 'greyDot'>, Spot[]> = {
  plank: [
    { id: 'marshNearIsland', zone: 'marshNear' }, { id: 'hearthHedges', zone: 'hearth' }, { id: 'ironwoodLog', zone: 'ironwood' }, { id: 'dunesRidge', zone: 'dunesOpen' },
  ],
  magnet: [
    { id: 'goldDais', zone: 'goldKeep' }, { id: 'hearthStream', zone: 'hearth' }, { id: 'marshShore', zone: 'marshNear' }, { id: 'ironwoodArch', zone: 'ironwood' },
  ],
  goldKey: [
    { id: 'marshShrine', zone: 'marshFar' }, { id: 'dunesBuried', zone: 'dunesBuried' }, { id: 'ironwoodRing', zone: 'ironwood' }, { id: 'cisternLedge', zone: 'cisternNear' }, { id: 'marshSmallIsland', zone: 'marshNear' },
  ],
  whiteKey: [
    { id: 'dunesBuried', zone: 'dunesBuried' }, { id: 'marshShrine', zone: 'marshFar' }, { id: 'hearthHedges', zone: 'hearth' }, { id: 'cisternLedge', zone: 'cisternNear' },
  ],
  blackKey: [
    { id: 'dunesCage', zone: 'dunesCaged' }, { id: 'whiteDais', zone: 'whiteKeep' }, { id: 'marshShrine', zone: 'marshFar' }, { id: 'goldDais', zone: 'goldKeep' }, { id: 'cisternFarLedge', zone: 'cisternFar' },
  ],
  lantern: [
    { id: 'whiteDais', zone: 'whiteKeep' }, { id: 'hearthSignpost', zone: 'hearth' }, { id: 'dunesObelisk', zone: 'dunesOpen' }, { id: 'ironwoodLog', zone: 'ironwood' },
  ],
};

export type Assignment = Record<keyof typeof CANDIDATES, string>;

function zoneOf(item: keyof typeof CANDIDATES, spotId: string): Zone {
  const s = CANDIDATES[item].find((c) => c.id === spotId);
  if (!s) throw new Error(`unknown spot ${spotId} for ${item}`);
  return s.zone;
}

/** Fixpoint: can the player obtain every key item and reach the Chalice (blackKeep) and the Gold Keep? */
export function solvable(a: Assignment, magpieHolds: ItemId | null = null): boolean {
  const have = new Set<ItemId>(['sword']);
  if (magpieHolds) have.add(magpieHolds); // the bird drops it in the open within two minutes
  const itemZone = new Map<ItemId, Zone>();
  for (const item of Object.keys(CANDIDATES) as (keyof typeof CANDIDATES)[]) {
    if (item === magpieHolds) continue;
    itemZone.set(item, zoneOf(item, a[item]));
  }
  const reachable = (z: Zone) => ZONE_NEEDS[z].every((n) => have.has(n));
  let changed = true;
  while (changed) {
    changed = false;
    for (const [item, z] of itemZone) {
      if (!have.has(item) && reachable(z)) { have.add(item); changed = true; }
    }
  }
  return reachable('blackKeep') && reachable('goldKeep');
}

/** Draw a solvable assignment. Also picks which key the Magpie starts with. */
export function shuffle(seed: number): { assignment: Assignment; magpieHolds: ItemId; tries: number } {
  const rng = new Rng(seed);
  const keys: ItemId[] = ['goldKey', 'whiteKey', 'blackKey'];
  for (let tries = 1; tries < 1000; tries++) {
    const a = {} as Assignment;
    for (const item of Object.keys(CANDIDATES) as (keyof typeof CANDIDATES)[]) a[item] = rng.pick(CANDIDATES[item]).id;
    const magpieHolds = rng.pick(keys);
    const ids = Object.values(a);
    if (new Set(ids).size !== ids.length) continue; // one item per spot
    if (solvable(a, magpieHolds)) return { assignment: a, magpieHolds, tries };
  }
  throw new Error('no solvable shuffle found');
}
